import { Link, Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { Button } from '@/components/ui';
import { ordersService } from '@/services';
import { ApiError } from '@/services/api';
import { useAuthStore } from '@/stores/auth.store';
import type { Order, OrderFulfillmentStatus, SellerOrderView } from '@/types/order';
import { formatPrice } from '@/utils/format';
import { allowedNextFulfillmentStatuses } from '@/utils/orderFulfillment';
import { operationalOrderStatusLabel } from '@/utils/orderOperationalDisplay';

export function SellerOrdersPage(): JSX.Element {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [selection, setSelection] = useState<Record<string, OrderFulfillmentStatus | ''>>({});
  const [commentByOrder, setCommentByOrder] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const { data: orders, isLoading, error } = useQuery({
    queryKey: ['orders', 'sales'],
    queryFn: () => ordersService.getMySales(),
    enabled: !!user && user.role === 'SELLER',
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      fulfillmentStatus,
      comment,
    }: {
      id: string;
      fulfillmentStatus: OrderFulfillmentStatus;
      comment?: string;
    }) => ordersService.updateOrderFulfillment(id, fulfillmentStatus, comment),
    onSuccess: async (_, variables) => {
      setFeedback(t('sellerOrders.updated'));
      setErrorMsg(null);
      setCommentByOrder((c) => ({ ...c, [variables.id]: '' }));
      setSelection((s) => ({ ...s, [variables.id]: '' }));
      await queryClient.invalidateQueries({ queryKey: ['orders', 'sales'] });
      await queryClient.invalidateQueries({ queryKey: ['orders', variables.id] });
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (e) => {
      setFeedback(null);
      setErrorMsg(e instanceof ApiError ? e.message : t('common.error'));
    },
  });

  const handleApply = (order: Order) => {
    const next = selection[order.id];
    if (!next || next === order.fulfillmentStatus) return;
    if (
      !window.confirm(
        t('sellerOrders.confirmFulfillment', {
          id: order.id.slice(0, 8),
          status: operationalOrderStatusLabel(next, t),
        }),
      )
    ) {
      return;
    }
    updateMutation.mutate({ id: order.id, fulfillmentStatus: next, comment: commentByOrder[order.id] });
  };

  if (!user) {
    return (
      <div className="flex min-h-[320px] items-center justify-center px-4">
        <p className="font-sans text-brand-700">{t('common.loading')}</p>
      </div>
    );
  }

  if (user.role === 'ADMINISTRATOR') {
    return <Navigate to="/admin/orders" replace />;
  }

  if (user.role !== 'SELLER') {
    return <Navigate to="/" replace />;
  }

  if (isLoading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center px-4">
        <p className="font-sans text-brand-700">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    const message =
      error instanceof ApiError ? error.message : t('orders.salesLoadError');
    return (
      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="rounded-md bg-red-50 p-4 text-red-800">{message}</div>
      </div>
    );
  }

  const list: SellerOrderView[] = orders ?? [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
      <h1 className="mb-6 font-heading text-heading-xl text-brand-950">{t('orders.salesTitle')}</h1>

      {feedback && (
        <div className="mb-4 rounded-md border border-green-200 bg-green-50 p-3 font-sans text-sm text-green-800">
          {feedback}
        </div>
      )}
      {errorMsg && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 font-sans text-sm text-red-800">
          {errorMsg}
        </div>
      )}

      {list.length === 0 ? (
        <p className="font-sans text-brand-700">{t('orders.salesEmpty')}</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-brand-700/20 bg-surface shadow-sm">
          <table className="min-w-full font-sans text-sm">
            <thead className="bg-brand-100 text-left text-brand-700">
              <tr>
                <th className="px-3 py-2 font-semibold">{t('orders.orderIdColumn')}</th>
                <th className="px-3 py-2 font-semibold">{t('adminOrders.buyer')}</th>
                <th className="px-3 py-2 font-semibold">{t('sellerOrders.subtotalYourLines')}</th>
                <th className="px-3 py-2 font-semibold">{t('adminOrders.date')}</th>
                <th className="px-3 py-2 font-semibold">{t('orders.fulfillmentColumn')}</th>
                <th className="px-3 py-2 font-semibold" />
              </tr>
            </thead>
            <tbody className="divide-y divide-brand-100">
              {list.map((order) => {
                const nextOpts = allowedNextFulfillmentStatuses(order.fulfillmentStatus);
                const sel = selection[order.id] ?? '';
                const busy = updateMutation.isPending && updateMutation.variables?.id === order.id;
                return (
                  <tr key={order.id} className="hover:bg-brand-100/50">
                    <td className="px-3 py-2 font-mono text-xs text-brand-950">{order.id.slice(0, 8)}…</td>
                    <td className="px-3 py-2 text-brand-950">
                      {order.buyer.displayName || order.buyer.email}
                      <span className="block text-xs text-brand-700">{order.buyer.email}</span>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-brand-950">
                      {formatPrice(order.sellerSubtotalCop ?? 0, 'COP')}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-brand-700">
                      {new Date(order.createdAt).toLocaleString()}
                    </td>
                    <td className="px-3 py-2">
                      <p className="mb-2 font-medium text-brand-950">
                        {operationalOrderStatusLabel(order.fulfillmentStatus, t)}
                      </p>
                      {nextOpts.length > 0 ? (
                        <div className="flex max-w-[220px] flex-col gap-1">
                          <select
                            className="rounded border border-brand-700/35 bg-surface px-2 py-1 text-brand-950"
                            value={sel}
                            onChange={(e) =>
                              setSelection((s) => ({
                                ...s,
                                [order.id]: (e.target.value || '') as OrderFulfillmentStatus | '',
                              }))
                            }
                            disabled={busy}
                          >
                            <option value="">{t('adminOrders.selectStatus')}</option>
                            {nextOpts.map((st) => (
                              <option key={st} value={st}>
                                {operationalOrderStatusLabel(st, t)}
                              </option>
                            ))}
                          </select>
                          <textarea
                            rows={2}
                            className="w-full rounded border border-brand-700/35 bg-surface px-2 py-1 text-xs text-brand-950"
                            placeholder={t('orders.changeCommentPlaceholder')}
                            value={commentByOrder[order.id] ?? ''}
                            onChange={(e) =>
                              setCommentByOrder((c) => ({ ...c, [order.id]: e.target.value }))
                            }
                            disabled={busy}
                          />
                          <Button
                            type="button"
                            size="sm"
                            onClick={() => handleApply(order)}
                            disabled={busy || !sel || sel === order.fulfillmentStatus}
                            loading={busy}
                          >
                            {busy ? t('common.loading') : t('adminOrders.apply')}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-brand-400">{t('adminOrders.fulfillmentComplete')}</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-xs text-brand-600 hover:text-brand-400 hover:underline"
                      >
                        {t('orders.viewDetail')} →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
